/**
 * Google Ads upload detail card for the qualified / converted lead detail
 * pages. Shows the ledger row for this contact + event type — the verbatim
 * Data Manager request payload (`event_payload`) and Google's response body —
 * so operators can debug uploads without SQL or Railway logs.
 *
 * Rendered when:
 *   • The hook returns a row (the upload was at least attempted).
 *   • The location is in google_ads mode (parent decides; we accept a prop).
 *
 * Empty state: a muted "no upload attempted yet" line so the section
 * unambiguously says nothing has happened — distinct from a failed upload.
 */

import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ChevronDown, ChevronRight, Copy, ExternalLink } from 'lucide-react'
import { formatRelativeTime } from '@/lib/ghl-qualifier'
import {
  useContactGoogleAdsConversion,
  type ContactGoogleAdsConversion,
  type QualifierConversionEventType,
} from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly contactId: string
  readonly eventType: QualifierConversionEventType
  /** When false, the card hides itself (used when the location isn't in google_ads mode). */
  readonly enabled?: boolean
}

/** Human-friendly event labels for the card header (vs. the raw slug). */
const EVENT_TITLES: Record<QualifierConversionEventType, string> = {
  qualified: 'qualified',
  converted: 'converted',
  form_submit: 'form submit',
  phone_call: 'phone call',
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  uploaded: { bg: 'bg-green-100', text: 'text-green-800' },
  validated: { bg: 'bg-blue-100', text: 'text-blue-800' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  processing: { bg: 'bg-slate-100', text: 'text-slate-700' },
  failed: { bg: 'bg-red-100', text: 'text-red-800' },
  skipped_no_identifier: { bg: 'bg-slate-100', text: 'text-slate-600' },
}

export function GoogleAdsUploadDetail({ contactId, eventType, enabled = true }: Props) {
  const query = useContactGoogleAdsConversion(contactId, eventType, enabled)

  if (!enabled) return null

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-semibold">
            Google Ads upload ({EVENT_TITLES[eventType]})
          </h3>
          {query.data?.request_id && (
            <span className="text-[11px] text-muted-foreground font-mono">
              requestId: {query.data.request_id}
            </span>
          )}
        </div>

        {query.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : !query.data ? (
          <p className="text-sm text-muted-foreground">
            No {EVENT_TITLES[eventType]} conversion has been uploaded for this contact yet.
          </p>
        ) : (
          <UploadDetailBody row={query.data} />
        )}
      </CardContent>
    </Card>
  )
}

function UploadDetailBody({ row }: { readonly row: ContactGoogleAdsConversion }) {
  const style = STATUS_COLORS[row.status] ?? { bg: 'bg-slate-100', text: 'text-slate-700' }
  const isValidate = row.validate_only === true

  return (
    <div className="space-y-4">
      {/* Headline row — status + when */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={`${style.bg} ${style.text} hover:${style.bg}`}>
          {row.status}
          {isValidate ? ' (test)' : ''}
        </Badge>
        {row.uploaded_at ? (
          <span className="text-xs text-muted-foreground">
            uploaded {formatRelativeTime(row.uploaded_at)}
          </span>
        ) : row.last_attempt_at ? (
          <span className="text-xs text-muted-foreground">
            last attempted {formatRelativeTime(row.last_attempt_at)}
          </span>
        ) : null}
        {row.attempt_count != null && row.attempt_count > 0 && (
          <span className="text-xs text-muted-foreground">
            · {row.attempt_count} attempt{row.attempt_count === 1 ? '' : 's'}
            {row.max_attempts ? ` of ${row.max_attempts}` : ''}
          </span>
        )}
        {row.response_status != null && (
          <span className="text-xs text-muted-foreground">
            · HTTP {row.response_status}
          </span>
        )}
      </div>

      {/* Key/value summary grid */}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
        <SummaryRow label="Transaction ID" value={row.transaction_id} mono />
        <SummaryRow
          label="Conversion action"
          value={row.conversion_action_id ?? '—'}
          mono
        />
        <SummaryRow
          label="Customer ID"
          value={row.google_ads_customer_id ?? '—'}
          mono
        />
        <SummaryRow label="Login customer (MCC)" value={row.login_customer_id ?? '—'} mono />
        <SummaryRow
          label="Conversion value"
          value={
            row.conversion_value != null
              ? `${row.conversion_value} ${row.currency ?? ''}`.trim()
              : '—'
          }
        />
        <SummaryRow
          label="Event timestamp"
          value={
            row.event_timestamp ? new Date(row.event_timestamp).toLocaleString() : '—'
          }
        />
        <SummaryRow label="GCLID" value={row.gclid ?? '—'} mono truncate />
        <SummaryRow
          label="Identifiers sent"
          value={
            [
              row.had_email ? 'email' : null,
              row.had_phone ? 'phone' : null,
              row.gclid ? 'gclid' : null,
            ]
              .filter(Boolean)
              .join(' + ') || '—'
          }
        />
      </dl>

      {/* Error summary (when present) */}
      {row.error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-xs font-medium text-red-800 mb-1">Error</p>
          <pre className="text-xs text-red-900 whitespace-pre-wrap break-words font-mono">
            {row.error}
          </pre>
        </div>
      )}

      {/* Collapsible JSON blocks */}
      <Collapsible
        label="Request payload (Data Manager events:ingest)"
        json={row.event_payload}
        defaultOpen={false}
      />
      <Collapsible
        label={`Response${row.response_status != null ? ` (HTTP ${row.response_status})` : ''}`}
        json={tryParseJson(row.response_body)}
        rawText={row.response_body}
        defaultOpen={false}
      />
    </div>
  )
}

interface SummaryRowProps {
  readonly label: string
  readonly value: string
  readonly mono?: boolean
  readonly truncate?: boolean
}

function SummaryRow({ label, value, mono, truncate }: SummaryRowProps) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-muted-foreground shrink-0">{label}:</dt>
      <dd
        className={[
          'text-foreground min-w-0',
          mono ? 'font-mono text-[11px]' : '',
          truncate ? 'truncate' : 'break-all',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </dd>
    </div>
  )
}

/**
 * Collapsible "Show payload" block. Renders pretty-printed JSON when parseable,
 * else the raw text. Copy button always copies whatever's displayed.
 */
function Collapsible({
  label,
  json,
  rawText,
  defaultOpen,
}: {
  readonly label: string
  readonly json: unknown
  readonly rawText?: string | null
  readonly defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(!!defaultOpen)
  const [copied, setCopied] = useState(false)

  const display =
    json !== undefined && json !== null
      ? JSON.stringify(json, null, 2)
      : rawText && rawText.length > 0
        ? rawText
        : null

  const handleCopy = async () => {
    if (!display) return
    try {
      await navigator.clipboard.writeText(display)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore — clipboard not available
    }
  }

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full px-3 py-2 text-xs font-medium hover:bg-muted/40"
      >
        <span className="flex items-center gap-1.5">
          {open ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          {label}
        </span>
        {display && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Copy"
            onClick={(e) => {
              e.stopPropagation()
              void handleCopy()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                void handleCopy()
              }
            }}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[11px] cursor-pointer"
          >
            <Copy className="h-3 w-3" />
            {copied ? 'copied' : 'copy'}
          </span>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-0 border-t bg-muted/20">
          {display ? (
            <pre className="text-[11px] font-mono whitespace-pre-wrap break-words leading-relaxed mt-2 max-h-80 overflow-y-auto">
              {display}
            </pre>
          ) : (
            <p className="text-xs text-muted-foreground mt-2">(no data)</p>
          )}
        </div>
      )}
    </div>
  )
}

function tryParseJson(text: string | null | undefined): unknown {
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

// Optional named export for external "open in Google Ads Diagnostics" link.
// Kept exported in case we want to add a deep link later.
export const _externalLinkIcon = ExternalLink
